import { useResponseRedirect } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  fromHomeManufactureDetails,
  toUpdateHomeManufactureDetailsReq,
  type HomeManufactureDetailsFormData,
} from "src/api/adapters";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import {
  FormTable,
  FormTableBody,
  Home02Icon,
  HomeFields,
} from "@porchpass/ui";
import { type DtoHome } from "@porchpass/api-client";
import { useMemo } from "react";
import { lessThanMaxLength, notEmpty, test, validate } from "src/utils";
import { testOtherTextRequired } from "src/forms/validation/conditionalOther";
import {
  DLA_HOME_MANUFACTURER_OTHER,
  DLA_HOME_SECTION_OTHER,
} from "src/forms/validation/conditionalOtherConfigs";
import {
  fromHomeManufactureFormData,
  homeManufactureErrorsAfterSubmit,
  toHomeManufactureFormData,
} from "src/forms/adapters/home";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";

const validationMapper = (formData: HomeManufactureDetailsFormData) => ({
  manufacturer_type: [test(notEmpty, "Must select Home Manufacturer")],
  section_type: [test(notEmpty, "Must select Home Section")],
  model_year: [test(notEmpty, "Must select Model Year")],
  manufacturer_type_other: [
    testOtherTextRequired(
      formData.manufacturer_type,
      DLA_HOME_MANUFACTURER_OTHER,
      undefined,
      "Must enter a manufacturer",
    ),
    test(lessThanMaxLength(70), "Must be less than 70 characters"),
  ],
  model_name: [
    test(notEmpty, "Must have Model Name"),
    test(lessThanMaxLength(70), "Must be less than 70 characters"),
  ],
  section_type_other: [
    testOtherTextRequired(
      formData.section_type,
      DLA_HOME_SECTION_OTHER,
      undefined,
      "Must enter a section type",
    ),
    test(lessThanMaxLength(120), "Must be less than 120 characters"),
  ],
});

export default () => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<HomeManufactureDetailsFormData>(
    {},
    () => api.getHome(),
    (data) => fromHomeManufactureDetails(data as DtoHome),
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: HomeManufactureDetailsFormData) =>
      api.updateHome(toUpdateHomeManufactureDetailsReq(data)),
    );
  useResponseRedirect("/home/down-payment", response);

  const formErrors = validate(formData as never, validationMapper as never);

  const homeValue = useMemo(
    () => toHomeManufactureFormData(formData),
    [formData],
  );

  const visibleErrors = fieldErrorsAfterSubmit(
    homeManufactureErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    hasAttemptedFormSubmission,
  );

  return (
    <FormLayout
      title="Tell us more about the home you'll be buying."
      headerTitle="Home and Land"
      stepIndex={7}
      footerTitle="Next"
      clickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Home Details"
          headerIcon={
            <Home02Icon
              variant="bulk-rounded"
              size={16}
              className="text-primary"
            />
          }
        >
          <FormTableBody>
            <HomeFields
              value={homeValue}
              onChange={(patch) => {
                const dlaPatch = fromHomeManufactureFormData({
                  ...homeValue,
                  ...patch,
                });
                for (const [key, value] of Object.entries(dlaPatch)) {
                  updateField(key as keyof HomeManufactureDetailsFormData)(
                    value,
                  );
                }
              }}
              errors={visibleErrors}
              showManufactureSection
            />
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
